import type { Header, HeaderGroup } from '@tanstack/react-table';
import { Fragment } from 'react';
import classNames from 'classnames';
import { Filtering, Sorting } from 'modules/table';
import { Typography } from 'modules/ui';
import { flexRender } from '@tanstack/react-table';

import styles from './Table.module.scss';

type TableHeadRowsProps = {
    headerGroups: HeaderGroup<any>[];
    hideBorders?: boolean;
    isEmbeddedVariant: boolean;
    setColumnWidthOnHeader: (headerIndex: number, header: Header<any, unknown>) => void;
    shouldHideResizing: boolean;
    showInlineFilters: boolean;
};

export const TableHeadRows = ({
    headerGroups,
    hideBorders,
    isEmbeddedVariant,
    setColumnWidthOnHeader,
    shouldHideResizing,
    showInlineFilters,
}: TableHeadRowsProps) => (
    <>
        {headerGroups.map((headerGroup) => (
            <Fragment key={headerGroup.id}>
                <tr className={classNames(styles.tr, { [styles.hideBorders]: hideBorders })}>
                    {headerGroup.headers.map((header, index) => {
                        setColumnWidthOnHeader(index, header);

                        const columnWidth = header.getSize();

                        return (
                            <th
                                key={header.id}
                                className={classNames(styles.th, {
                                    [styles.hideBorders]: hideBorders,
                                    [styles.hidden]: !header.column.getIsVisible(),
                                })}
                                style={{ width: `${columnWidth}px`, minWidth: `${columnWidth}px` }}
                            >
                                <div className={styles['th-header-row']}>
                                    <div className={styles['th-container']}>
                                        <Sorting header={header}>
                                            <Typography
                                                variant="h6"
                                                className={classNames({
                                                    [styles.centerHeader]: header.column.columnDef.meta?.centerHeader,
                                                })}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </Typography>
                                        </Sorting>
                                        {isEmbeddedVariant && <Filtering column={header.column} />}
                                    </div>
                                </div>
                                {!shouldHideResizing && (
                                    <div
                                        className={classNames(styles.resizer)}
                                        style={{
                                            backgroundColor: header.column.getIsResizing() ? '#1770ff' : undefined,
                                        }}
                                        onDoubleClick={() => header.column.resetSize()}
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                    />
                                )}
                            </th>
                        );
                    })}
                </tr>
                {showInlineFilters && (
                    <tr className={classNames(styles.filterRow, { [styles.hideBorders]: hideBorders })}>
                        {headerGroup.headers.map((header) => {
                            const columnWidth = header.getSize();

                            return (
                                <th
                                    key={`${header.id}-filter`}
                                    className={classNames(styles.filterCell, {
                                        [styles.hideBorders]: hideBorders,
                                        [styles.hidden]: !header.column.getIsVisible(),
                                    })}
                                    style={{ width: `${columnWidth}px`, minWidth: `${columnWidth}px` }}
                                >
                                    <Filtering column={header.column} inline />
                                </th>
                            );
                        })}
                    </tr>
                )}
            </Fragment>
        ))}
    </>
);
